import type { AccessGroup } from '@/api/core/access-groups';
import {
  deleteAccessGroup,
  readAccessGroup,
  readAccessGroupRules,
  updateAccessGroup,
} from '@/api/core/access-groups';
import { readGroupMembers } from '@/api/core/access-members';
import { canReadDomain, coreQueryKey } from '@/api/core/authorization';
import { isApiError } from '@/api/http/errors';
import { ShellHeader } from '@/app/shell-header';
import { useOperatorSession } from '@/auth/runtime';
import { isUuid } from '@/lib/validation';
import {
  Button,
  DetailPage,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mgrvtechgroup/admin-ds';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { propertyRuntime, type PropertyRuntime } from '../property/property-queries';
import { GroupAccessManagement } from './group-access-management';

/** 현재 그룹, 기기 규칙 및 마스킹된 멤버를 표시한다. */
export function AccessGroupDetailPage({
  runtime = propertyRuntime,
}: {
  runtime?: PropertyRuntime;
}) {
  const { groupId = '' } = useParams();
  const session = useOperatorSession(runtime.session);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [memberPage, setMemberPage] = useState<{
    groupId: string;
    generation: number;
    cursor: string;
  }>();
  const memberCursor =
    memberPage?.groupId === groupId && memberPage.generation === session.generation
      ? memberPage.cursor
      : undefined;
  const [name, setName] = useState<string>();
  const [floor, setFloor] = useState<string>();
  const [role, setRole] = useState<NonNullable<AccessGroup['staff_role']>>();
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [message, setMessage] = useState('');
  const allowed =
    session.status === 'authorized' &&
    !!session.authorization &&
    !session.deniedDomains.includes('entitlement') &&
    canReadDomain(session.authorization, 'entitlement', 1) &&
    isUuid(groupId);
  const group = useQuery({
    queryKey: coreQueryKey(runtime.apiNamespace, session.generation, 'access-group', groupId),
    queryFn: ({ signal }) => readAccessGroup(runtime.client, groupId, signal),
    enabled: allowed,
  });
  const rules = useQuery({
    queryKey: coreQueryKey(runtime.apiNamespace, session.generation, 'access-group-rules', groupId),
    queryFn: ({ signal }) => readAccessGroupRules(runtime.client, groupId, signal),
    enabled: allowed,
  });
  const members = useQuery({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, session.generation, 'access-group-members', groupId),
      memberCursor ?? '',
    ],
    queryFn: ({ signal }) => readGroupMembers(runtime.client, groupId, signal, memberCursor),
    enabled: allowed,
  });
  const writable =
    allowed &&
    !!session.authorization &&
    canReadDomain(session.authorization, 'entitlement', 4) &&
    group.data?.origin === 'CORE';
  const mutateGroup = async (deleting: boolean) => {
    const current = group.data;
    const actor = runtime.session.getSnapshot();
    if (
      !writable ||
      actor.generation !== session.generation ||
      actor.status !== 'authorized' ||
      !actor.authorization ||
      actor.deniedDomains.includes('entitlement') ||
      !canReadDomain(actor.authorization, 'entitlement', 4) ||
      busy ||
      !current ||
      (deleting && !confirmDelete)
    )
      return;
    const nextFloor = current.kind === 'STAFF' ? null : Number(floor ?? current.floor);
    if (
      !deleting &&
      current.kind !== 'STAFF' &&
      ((floor ?? String(current.floor)).trim() === '' || !Number.isSafeInteger(nextFloor))
    ) {
      setMessage('그룹 층을 확인해 주세요.');
      return;
    }
    const generation = session.generation;
    setBusy(true);
    setMessage('');
    try {
      const command = { command_id: crypto.randomUUID(), expected_revision: current.revision };
      if (deleting) await deleteAccessGroup(runtime.client, groupId, command);
      else
        await updateAccessGroup(runtime.client, groupId, {
          ...command,
          name: (name ?? current.name).trim(),
          kind: current.kind,
          property_id: current.property_id,
          floor: nextFloor,
          staff_role: current.kind === 'STAFF' ? (role ?? current.staff_role) : null,
        });
      if (runtime.session.getSnapshot().generation !== generation) return;
      if (deleting) void navigate('/access/groups');
      else {
        setName(undefined);
        setFloor(undefined);
        setRole(undefined);
        setMessage('그룹을 저장했습니다.');
        await group.refetch();
      }
      await qc.invalidateQueries({
        queryKey: coreQueryKey(runtime.apiNamespace, generation, 'access-groups'),
      });
    } catch (error) {
      if (runtime.session.getSnapshot().generation !== generation) return;
      if (isApiError(error) && error.status === 403)
        runtime.session.reportDomainAccessDenied('entitlement', generation);
      setMessage(
        isApiError(error) && error.status === 409
          ? '그룹이 변경되었거나 활성 멤버가 있습니다. 다시 조회해 주세요.'
          : '그룹을 변경하지 못했습니다.',
      );
    } finally {
      setBusy(false);
    }
  };
  if (session.status !== 'authorized') return null;
  return (
    <DetailPage className="min-h-0 flex-1 overflow-y-auto">
      <ShellHeader>
        <PageHeader title="출입 그룹 상세" />
      </ShellHeader>
      <DetailPage.Body>
        <Button
          nativeButton={false}
          role="link"
          variant="outline"
          render={<Link to="/access/groups" />}
        >
          그룹 목록
        </Button>
        {allowed ? null : <p role="alert">출입 그룹을 조회할 수 없습니다.</p>}
        {group.isPending && allowed ? <p role="status">그룹을 조회하고 있습니다.</p> : null}
        {group.isError || rules.isError || members.isError ? (
          <p role="alert">그룹 정보를 불러오지 못했습니다.</p>
        ) : null}
        {allowed && group.data ? (
          <DetailPage.Section title={group.data.name}>
            <p>
              {group.data.kind} · revision {group.data.revision}
            </p>
            {group.data.origin === 'LEGACY_SYNC' ? <p>동기화 그룹은 읽기 전용입니다.</p> : null}
            {writable ? (
              <div className="space-y-2">
                <label>
                  그룹 이름
                  <Input
                    value={name ?? group.data.name}
                    maxLength={100}
                    onChange={(event) => {
                      setName(event.target.value);
                    }}
                  />
                </label>
                {group.data.kind === 'STAFF' ? (
                  <Select
                    value={role ?? group.data.staff_role ?? 'ADMIN'}
                    onValueChange={(value) => {
                      if (value) setRole(value);
                    }}
                  >
                    <SelectTrigger aria-label="직원 역할">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(['ADMIN', 'MANAGER', 'FM', 'HK', 'GUARD', 'RETAIL'] as const).map(
                        (value) => (
                          <SelectItem key={value} value={value}>
                            {value}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                ) : (
                  <label>
                    그룹 층
                    <Input
                      value={floor ?? String(group.data.floor ?? '')}
                      onChange={(event) => {
                        setFloor(event.target.value);
                      }}
                    />
                  </label>
                )}
                <Button
                  disabled={busy || !(name ?? group.data.name).trim()}
                  onClick={() => void mutateGroup(false)}
                >
                  그룹 저장
                </Button>
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => {
                    setConfirmDelete(true);
                  }}
                >
                  그룹 삭제
                </Button>
                {confirmDelete ? (
                  <div role="alert">
                    <p>그룹을 삭제할까요?</p>
                    <Button disabled={busy} onClick={() => void mutateGroup(true)}>
                      삭제 확인
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setConfirmDelete(false);
                      }}
                    >
                      돌아가기
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}
            {message ? <p role="status">{message}</p> : null}
          </DetailPage.Section>
        ) : null}
        {allowed && rules.data ? (
          <DetailPage.Section title="기기와 시간표">
            <ul>
              {rules.data.map((rule) => (
                <li key={rule.id}>
                  {rule.device_id} · {rule.floor === null ? '문' : `${String(rule.floor)}층`} ·{' '}
                  {Object.entries(rule.schedule)
                    .map(([day, values]) => `${day}: ${values.join(', ')}`)
                    .join(' / ')}
                </li>
              ))}
            </ul>
          </DetailPage.Section>
        ) : null}
        {writable && group.data && rules.data && members.data ? (
          <GroupAccessManagement
            key={`${String(session.generation)}:${String(group.data.revision)}`}
            runtime={runtime}
            generation={session.generation}
            group={group.data}
            rules={rules.data}
            members={members.data.items}
            reload={async () => {
              await Promise.all([group.refetch(), rules.refetch(), members.refetch()]);
            }}
          />
        ) : null}
        {allowed && members.data ? (
          <DetailPage.Section title="출입 멤버">
            <ul>
              {members.data.items.map((member) => (
                <li key={member.id}>
                  {member.name ?? member.user_id ?? '비회원'} · {member.phone_masked ?? '—'} ·{' '}
                  {member.source_kind}
                </li>
              ))}
            </ul>
            {members.data.next_cursor ? (
              <Button
                onClick={() => {
                  if (members.data.next_cursor)
                    setMemberPage({
                      groupId,
                      generation: session.generation,
                      cursor: members.data.next_cursor,
                    });
                }}
              >
                다음 멤버
              </Button>
            ) : null}
            {memberCursor ? (
              <Button
                variant="outline"
                onClick={() => {
                  setMemberPage(undefined);
                }}
              >
                처음 멤버
              </Button>
            ) : null}
          </DetailPage.Section>
        ) : null}
      </DetailPage.Body>
    </DetailPage>
  );
}
